'use client';

/**
 * @aumonline/team — drop-in Team management module.
 *
 * The host injects everything host-specific via props (storage,
 * currentUser, costume, copy). The module has zero knowledge of
 * the backend stack — MongoDB, Supabase, Firebase, anything works
 * as long as the host writes the adapter.
 *
 * Usage:
 *   import { TeamModule } from "../packages/team";
 *   <TeamModule storage={teamStorage} currentUser={user} />
 *
 * Contract: see README.md and TEAM_STORAGE_INTERFACE.md inside
 * this folder.
 */

import React from "react";
import { TeamPage } from "./TeamPage";
import { TeamContext, defaultCostume, defaultCopy } from "./internals";
import { ToastProvider } from "./components/Toaster";

export const TeamModule = ({
  storage,
  currentUser,
  costume,
  copy,
  onAuditEvent,
}) => {
  if (!storage) {
    throw new Error("<TeamModule> requires a `storage` prop. See TEAM_STORAGE_INTERFACE.md.");
  }
  if (!currentUser?.role) {
    throw new Error("<TeamModule> requires `currentUser` with at least { role }.");
  }

  const value = React.useMemo(
    () => ({
      storage,
      currentUser,
      costume: { ...defaultCostume, ...(costume || {}) },
      copy: { ...defaultCopy, ...(copy || {}) },
      onAuditEvent: onAuditEvent || (() => {}),
    }),
    [storage, currentUser, costume, copy, onAuditEvent]
  );

  return (
    <TeamContext.Provider value={value}>
      <ToastProvider>
        <TeamPage />
      </ToastProvider>
    </TeamContext.Provider>
  );
};
